import type { History } from "history";
import React from "react";
import { Provider } from "react-redux";
import { Router } from "react-router-dom";
import type { Store } from "redux";

import type { Impulse } from "~/tools/impulse";

import type { RootState } from "./app/state/RootState";
import { PinnedItemsProvider, type PinnedItemsState } from "./bottom_content/view/panes/east/pinned-items";
import { SouthPaneProvider } from "./bottom_content/view/panes/south/south-pane-provider";
import type { SouthPaneState } from "./bottom_content/view/panes/south/use-south-pane-state";
import { WebSocketProvider } from "./components/_websocket";
import { BrowserPageTitleProvider } from "./components/browser-page-title";
import { ToggleListStateProvider } from "./components/react/_toggle-list";
import {
	ExplorationDrawerContent,
	ExplorationDrawerProvider,
} from "./components/react/drawer/ExplorationDrawerProvider";
import { TimeRangeHistoryProvider } from "./components/react/timeline/TimeRangeHistory";
import type { TimeRangeHistory } from "./components/react/timeline/time-range-history-utils";
import { RootApplcationDomainProvider } from "./components/root-application-domain-provider";
import { TimestampCursorProvider } from "./components/TimestampCursor";
import { DashboardProvider } from "./routes/dashboards/dashboard/provider";

export const ReduxStoreProvider: React.FC<React.PropsWithChildren<{ store: Store<RootState> }>> = (props) => (
	<Provider {...props} />
);

export const RouterProvider: React.FC<React.PropsWithChildren<{ history: History }>> = (props) => <Router {...props} />;

export interface ProvidersProps {
	store: Store<RootState>;
	history: History;
	pinnedItemsState?: PinnedItemsState;
	timeRangeHistory?: Impulse<TimeRangeHistory>;
	southPane?: SouthPaneState;
}

export const Providers: React.FC<React.PropsWithChildren<ProvidersProps>> = ({
	store,
	history,
	pinnedItemsState,
	timeRangeHistory,
	southPane,
	children,
}) => (
	<ReduxStoreProvider store={store}>
		<WebSocketProvider>
			<RouterProvider history={history}>
				<RootApplcationDomainProvider>
					<BrowserPageTitleProvider>
						<TimestampCursorProvider>
							<TimeRangeHistoryProvider history={timeRangeHistory}>
								<PinnedItemsProvider initialState={pinnedItemsState}>
									<ToggleListStateProvider>
										<ExplorationDrawerProvider>
											<SouthPaneProvider state={southPane}>
												<DashboardProvider>
													{children}
													<ExplorationDrawerContent />
												</DashboardProvider>
											</SouthPaneProvider>
										</ExplorationDrawerProvider>
									</ToggleListStateProvider>
								</PinnedItemsProvider>
							</TimeRangeHistoryProvider>
						</TimestampCursorProvider>
					</BrowserPageTitleProvider>
				</RootApplcationDomainProvider>
			</RouterProvider>
		</WebSocketProvider>
	</ReduxStoreProvider>
);
